import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useNavigate } from 'react-router-dom';
import { useToaster } from '../../context/ToasterContext';
import { addWordToVocabulary, deleteWord, getAllVocabulary, getWordExplanation, handleSpeak } from '../../service/ApiService';
import { ACCESS_TOKEN } from '../../service/AuthService';
import WordLevelDialog from './WordLevelDialog';

interface Word {
  id: number;
  text: string;
  level?: string;
  showDelete: boolean;
}

interface VocabularyTabProps {
  words: Word[];
  setWords: (words: Word[]) => void;
}

export interface VocabularyTabRef {
  addWord: () => Promise<void>;
  refreshVocabulary: () => void;
}

const VocabularyTab = forwardRef<VocabularyTabRef, VocabularyTabProps>((props, ref): React.ReactElement => {
  const [vocabularyLoaded, setVocabularyLoaded] = useState(false);
  const [vocabularyLoading, setVocabularyLoading] = useState(false);
  const [speakingWordId, setSpeakingWordId] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [explanationText, setExplanationText] = useState('');
  const [explanationLoading, setExplanationLoading] = useState(false);
  const [showLevelDialog, setShowLevelDialog] = useState(false);
  const { showError, showSuccess } = useToaster();
  const navigate = useNavigate();
  const { words, setWords } = props;

  // Process the vocabulary response and update the words state, 
  // used by addWord, deleteWord, refreshVocabulary
  const processVocabularyResponse = (res: any) => {
    if (res && Array.isArray(res)) {
      setWords(res.map((item: any, index: number) => ({ 
        id: index, 
        text: item.word, 
        level: item.level, 
        showDelete: true 
      })));
      setVocabularyLoaded(true);
    } else {
      setWords([]);
      setVocabularyLoaded(true);
    }
  };

  const fetchVocabulary = async () => {
    // Only fetch if words are empty
    if (words.length > 0) {
      return;
    }
    
    setVocabularyLoading(true);
    try {
      const vocabulary = await getAllVocabulary().catch(error => {
        if (error.response?.status === 401) {
          localStorage.removeItem(ACCESS_TOKEN);
          navigate('/login');
        } else {
          showError('Failed to load vocabulary');
        }
      });
      
      processVocabularyResponse(vocabulary);
    } catch (error) {
      console.error('Error fetching vocabulary:', error);
      showError('Failed to load vocabulary');
      setWords([]);
      setVocabularyLoaded(true);
    } finally {
      setVocabularyLoading(false);
    }
  };

  const validateWord = async (word: string): Promise<boolean> => {
    try {
      const response = await fetch(`https://api.dictionaryapi.dev/api/v2/entries/en/${word.toLowerCase()}`);
      return response.ok;
    } catch (error) {
      console.error('Error validating word:', error);
      return false;
    }
  };

  const addWord = async () => {
    const text = prompt('Enter a word');
    if (text) {
      const trimmedWord = text.trim();
    
      if (!trimmedWord) {
        showError('Please enter a word');
        return;
      }
  
      try {
        //TODO
        //const isValid = await validateWord(trimmedWord);
        const isValid = true; // Temporarily bypass validation
        if (isValid) {
          const res = await addWordToVocabulary(trimmedWord);

          processVocabularyResponse(res);

          //showSuccess(`"${trimmedWord}" added to vocabulary!`);
        } else {
          showError(`"${trimmedWord}" is not a valid English word. Please try another word.`);
        }
      } catch (error) {
        showError('Error validating word. Please try again.');
      }
    }
  };

  const refreshVocabulary = () => {
    setVocabularyLoaded(false);
    setWords([]); // Clear words to force a fresh fetch
    fetchVocabulary();
  };

  const readWord = async (word: string) => {
    setSpeakingWordId(words.find(w => w.text === word)?.id);
    await handleSpeak(word);
    setSpeakingWordId(null);
  };

  const handleGetWordExplanation = async (word: string) => {
    setExplanationLoading(true);
    setShowExplanation(true);
    setExplanationText('Loading...');
    
    try {
      const explanation = await getWordExplanation(word);
      setExplanationText(explanation);
    } catch (error) {
      console.error('Error getting word explanation:', error);
      setExplanationText('Failed to load explanation. Please try again.');
      showError('Failed to get word explanation');
    } finally {
      setExplanationLoading(false);
    }
  };

  const deleteWordFromVocabulary = async (id: number) => {
    try {
      const wordToDelete = words.find(w => w.id === id);
      if (wordToDelete) {

        const res = await deleteWord(id);
        
        processVocabularyResponse(res);
        
        showSuccess(`"${wordToDelete.text}" deleted from vocabulary!`);
      }
    } catch (error) {
      console.error('Error deleting word:', error);
      showError('Failed to delete word from vocabulary');
    }
  };

  const handleTouchStart = (id: number, e: React.TouchEvent<HTMLLIElement>) => {
    // This would need to be implemented if you want to keep the swipe-to-delete functionality
    // For now, we'll keep it simple
  };

  const handleTouchEnd = (id: number, e: React.TouchEvent<HTMLLIElement>) => {
    // This would need to be implemented if you want to keep the swipe-to-delete functionality
    // For now, we'll keep it simple
  };

  // Function to get color for different CEFR levels
  const getLevelColor = (level: string): string => {
    switch (level?.toUpperCase()) {
      case 'A1':
        return '#4CAF50'; // Green
      case 'A2':
        return '#8BC34A'; // Light Green
      case 'B1':
        return '#FFC107'; // Amber
      case 'B2':
        return '#FF9800'; // Orange
      case 'C1':
        return '#F44336'; // Red
      case 'C2':
        return '#9C27B0'; // Purple
      default:
        return '#607D8B'; // Blue Grey
    }
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    addWord,
    refreshVocabulary
  }));

  // Load vocabulary when component mounts or when words are empty
  useEffect(() => {
    if (words.length === 0) {
      fetchVocabulary();
    }
  }, [words.length]);

  return (
    <div className="content vocabulary">
      {vocabularyLoading ? (
        <div className="loading-container">
          <div className="loading-spinner">...</div>
        </div>
      ) : (
        <ul className="word-list">
          {words.map(word => (
            <li
              key={word.id}
              className="word-item"
              onTouchStart={e => handleTouchStart(word.id, e)}
              onTouchEnd={e => handleTouchEnd(word.id, e)}
            >
              <span className="word-text">{word.text}</span>
              {word.showDelete && (
                <div className="action-buttons">
                  {/* Level button */}
                  <button
                    className="action-btn level-btn"
                    onClick={() => setShowLevelDialog(true)}
                    type="button"
                    style={{
                      backgroundColor: getLevelColor(word.level),
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '8px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      marginRight: '4px',
                      border: 'none',
                      cursor: 'pointer',
                      minWidth: 'auto',
                      height: 'auto'
                    }}
                  >
                    {word.level}
                  </button>
                  
                  <button
                    className="action-btn book-btn"
                    onClick={() => handleGetWordExplanation(word.text)}
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M4 6H2V20C2 21.1 2.9 22 4 22H18V20H4V6ZM20 2H8C6.9 2 6 2.9 6 4V16C6 17.1 6.9 18 8 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H8V4H20V16Z"/>
                      <path d="M10 9H18V11H10V9ZM10 12H14V14H10V12ZM10 6H18V8H10V6Z"/>
                    </svg>
                  </button>
                  <button
                    className="action-btn voice-btn"
                    onClick={() => readWord(word.text)}
                    type="button"
                    disabled={speakingWordId === word.id}
                  >
                    {speakingWordId === word.id ? (
                      <div className="loading-spinner" style={{ width: '16px', height: '16px', marginRight: '0' }}></div>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 14c1.66 0 3-1.34 3-3V5c0-1.66-1.34-3-3-3S9 3.34 9 5v6c0 1.66 1.34 3 3 3z"/>
                        <path d="M17 11c0 2.76-2.24 5-5 5s-5-2.24-5-5H5c0 3.53 2.61 6.43 6 6.92V21h2v-3.08c3.39-.49 6-3.39 6-6.92h-2z"/>
                      </svg>
                    )}
                  </button>
                  <button
                    className="action-btn delete-btn"
                    onClick={() => deleteWordFromVocabulary(word.id)}
                    type="button"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                  </button>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
      
      {/* Word Level Dialog */}
      <WordLevelDialog 
        isOpen={showLevelDialog}
        onClose={() => setShowLevelDialog(false)}
      />
      
      {/* Word Explanation Popup */}
      {showExplanation && (
        <div className="popup-overlay" style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="popup-content" style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '20px',
            margin: '20px',
            maxWidth: '90%',
            maxHeight: '80%',
            overflow: 'auto',
            position: 'relative'
          }}>
            <button
              onClick={() => setShowExplanation(false)}
              style={{
                position: 'absolute',
                top: '10px',
                right: '15px',
                background: 'none',
                border: 'none',
                fontSize: '20px',
                cursor: 'pointer',
                color: '#666'
              }}
            >
              ×
            </button>
            <div style={{ marginTop: '10px' }}>
              {explanationLoading ? (
                <div style={{ textAlign: 'center', padding: '20px' }}>
                  <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                  <p>Loading explanation...</p>
                </div>
              ) : (
                <div style={{ lineHeight: '1.6' }}>
                  <ReactMarkdown
                    components={{
                      h1: ({ children }) => <h1 style={{ fontSize: '18px', fontWeight: 'bold', margin: '10px 0', color: '#333' }}>{children}</h1>,
                      h2: ({ children }) => <h2 style={{ fontSize: '16px', fontWeight: 'bold', margin: '8px 0', color: '#333' }}>{children}</h2>,
                      h3: ({ children }) => <h3 style={{ fontSize: '14px', fontWeight: 'bold', margin: '6px 0', color: '#333' }}>{children}</h3>,
                      p: ({ children }) => <p style={{ margin: '8px 0', color: '#555' }}>{children}</p>,
                      strong: ({ children }) => <strong style={{ fontWeight: 'bold', color: '#333' }}>{children}</strong>,
                      em: ({ children }) => <em style={{ fontStyle: 'italic', color: '#555' }}>{children}</em>,
                      ul: ({ children }) => <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ul>,
                      ol: ({ children }) => <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>{children}</ol>,
                      li: ({ children }) => <li style={{ margin: '4px 0', color: '#555' }}>{children}</li>,
                      code: ({ children }) => <code style={{ 
                        backgroundColor: '#f4f4f4', 
                        padding: '2px 4px', 
                        borderRadius: '3px', 
                        fontFamily: 'monospace',
                        fontSize: '12px',
                        color: '#333'
                      }}>{children}</code>,
                      blockquote: ({ children }) => <blockquote style={{ 
                        borderLeft: '3px solid #007bff', 
                        margin: '8px 0', 
                        paddingLeft: '10px', 
                        color: '#666',
                        fontStyle: 'italic'
                      }}>{children}</blockquote>
                    }}
                  >
                    {explanationText}
                  </ReactMarkdown>
                </div>
              )}
              
              {/* Close button at the bottom */}
              <div style={{ 
                textAlign: 'center', 
                marginTop: '20px', 
                paddingTop: '15px', 
                borderTop: '1px solid #eee' 
              }}>
                <button
                  onClick={() => setShowExplanation(false)}
                  style={{
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '6px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    minWidth: '80px'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.backgroundColor = '#0056b3';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = '#007bff';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

VocabularyTab.displayName = 'VocabularyTab';

export default VocabularyTab;
